/* ============================================================================
 * ตัวเลือกกลางของ "ลูกค้า" (customers.js) และ "นัดหมาย" (appointments.js)
 *
 * สองหน้านี้ใช้ข้อมูลชุดเดียวกัน — นัดหมายทุกรายการเกิดจากลูกค้าที่ติดต่อเข้ามา
 * ถ้าแยกกันประกาศจะหลุดกันได้ง่าย (เช่น เปลี่ยนสถานะลูกค้าแล้วนัดหมายไม่ตาม)
 *
 * สำคัญ : ข้อมูลลูกค้า "ไม่ได้สร้างในระบบนี้" — ลูกค้ากรอกฟอร์มหน้าเว็บ / ทักแชท
 * / โทรเข้ามา แล้วระบบต้นทางส่งเข้ามาให้ หน้าลูกค้าจึงเป็นหน้า "ติดตามผล"
 * (อัปเดตสถานะ / มอบหมายผู้ดูแล / บันทึกกิจกรรม / นัดชมทรัพย์) ไม่มีปุ่มเพิ่มลูกค้า
 *
 * ไฟล์นี้เหลือเฉพาะ "ตัวเลือกคงที่" (สถานะ ช่องทาง ระดับความสนใจ ประเภท/สถานะนัด)
 * ข้อมูลจริงทั้งหมดมาจาก API แล้ว — ลูกค้า ทรัพย์ และผู้ดูแลอยู่ที่
 *   GET admin/customers · GET admin/assets · GET admin/users
 * ========================================================================== */

/* ============================================================================
 * สถานะการติดตามลูกค้า (pipeline)
 * เรียงตามลำดับจริงของงานขาย : ใหม่ → ติดต่อแล้ว → นัดชมทรัพย์ → ปิดดีล
 * ปิดไม่สำเร็จ (lost) เป็นปลายทางอีกทางหนึ่ง จึงไม่นับอยู่ในลำดับ pipeline
 *
 * เดิมมี "สนใจจริง" (qualified) กับ "กำลังเจรจา" (negotiating) คั่นอยู่ แต่ตัดออกแล้ว —
 * ระดับความสนใจ (hot/warm/cold) บอกเรื่องคัดกรองได้อยู่แล้ว ส่วนช่วงเจรจาดูจากประเภท
 * นัดหมาย (contract/handover) ได้ตรงกว่า ข้อมูลเก่าถูกย้ายไป contacted / appointment
 * color = ตัวอักษร/ไอคอน, tint = พื้น, line = ขอบ — ผ่านการตรวจ contrast แล้ว
 * ========================================================================== */
export const LEAD_STATUSES = [
  { value: "new", label: "lead_new", icon: "", color: "#0369a1", tint: "#e0f2fe", line: "#bae6fd", step: 1 },
  { value: "contacted", label: "lead_contacted", icon: "", color: "#0e7490", tint: "#cffafe", line: "#a5f3fc", step: 2 },
  { value: "appointment", label: "lead_appointment", icon: "", color: "#b45309", tint: "#fef3c7", line: "#fde68a", step: 3 },
  { value: "won", label: "lead_won", icon: "", color: "#15803d", tint: "#dcfce7", line: "#bbf7d0", step: 4 },
  { value: "lost", label: "lead_lost", icon: "", color: "#9f1239", tint: "#ffe4e6", line: "#fecdd3", step: 0 },
];

/* ขั้นตอนที่เอาไปวาดเป็นกรวย/แถบ pipeline (ไม่รวม "ไม่สำเร็จ") */
export const PIPELINE = LEAD_STATUSES.filter((s) => s.step > 0);

export const statusMeta = (value) => LEAD_STATUSES.find((e) => e.value === value) || LEAD_STATUSES[0];

/* สถานะที่ถือว่าจบงานแล้ว — ใช้ตัดออกจากยอด "กำลังติดตาม" */
export const isClosedStatus = (value) => value === "won" || value === "lost";

/* ============================================================================
 * ช่องทางที่ลูกค้าติดต่อเข้ามา — มาจากระบบต้นทาง แก้ในระบบนี้ไม่ได้
 * ========================================================================== */
export const LEAD_SOURCES = [
  { value: "website", label: "source_website", icon: "", color: "#0369a1", tint: "#e0f2fe" },
  { value: "line", label: "source_line", icon: "", color: "#15803d", tint: "#dcfce7" },
  { value: "facebook", label: "source_facebook", icon: "", color: "#1d4ed8", tint: "#e0e7ff" },
  { value: "phone", label: "source_phone", icon: "", color: "#b45309", tint: "#fef3c7" },
  { value: "walk_in", label: "source_walk_in", icon: "", color: "#6d28d9", tint: "#ede9fe" },
  { value: "referral", label: "source_referral", icon: "", color: "#0e7490", tint: "#cffafe" },
  { value: "partner", label: "source_partner", icon: "", color: "#be185d", tint: "#fce7f3" },
];

export const sourceMeta = (value) => LEAD_SOURCES.find((e) => e.value === value) || LEAD_SOURCES[0];

/* ============================================================================
 * ระดับความสนใจ — ระบบต้นทางให้มาเป็นคะแนน แปลงเป็น 3 ระดับให้อ่านง่าย
 * ========================================================================== */
export const LEAD_GRADES = [
  { value: "hot", label: "grade_hot", color: "#dc2626", tint: "#fee2e2", dots: 3 },
  { value: "warm", label: "grade_warm", color: "#ea580c", tint: "#ffedd5", dots: 2 },
  { value: "cold", label: "grade_cold", color: "#64748b", tint: "#e2e8f0", dots: 1 },
];

export const gradeMeta = (value) => LEAD_GRADES.find((e) => e.value === value) || LEAD_GRADES[2];

/* ============================================================================
 * ประเภทและสถานะของนัดหมาย
 * ========================================================================== */
export const APPOINTMENT_TYPES = [
  { value: "viewing", label: "appt_viewing", icon: "", color: "#4d7c0f", tint: "#eaf9d2" },
  { value: "meeting", label: "appt_meeting", icon: "", color: "#0369a1", tint: "#e0f2fe" },
  { value: "contract", label: "appt_contract", icon: "", color: "#6d28d9", tint: "#ede9fe" },
  { value: "handover", label: "appt_handover", icon: "", color: "#b45309", tint: "#fef3c7" },
];

export const apptTypeMeta = (value) => APPOINTMENT_TYPES.find((e) => e.value === value) || APPOINTMENT_TYPES[0];

export const APPOINTMENT_STATUSES = [
  { value: "pending", label: "appt_pending", icon: "", color: "#b45309", tint: "#fef3c7", line: "#fde68a" },
  { value: "confirmed", label: "appt_confirmed", icon: "", color: "#0369a1", tint: "#e0f2fe", line: "#bae6fd" },
  { value: "done", label: "appt_done", icon: "", color: "#15803d", tint: "#dcfce7", line: "#bbf7d0" },
  { value: "cancelled", label: "appt_cancelled", icon: "", color: "#64748b", tint: "#e2e8f0", line: "#cbd5e1" },
  { value: "no_show", label: "appt_no_show", icon: "", color: "#9f1239", tint: "#ffe4e6", line: "#fecdd3" },
];

export const apptStatusMeta = (value) => APPOINTMENT_STATUSES.find((e) => e.value === value) || APPOINTMENT_STATUSES[0];

/* สถานะที่ยังต้องทำงานต่อ — ใช้กรอง "นัดที่กำลังจะถึง" */
export const isOpenAppointment = (value) => value === "pending" || value === "confirmed";
